import AppContext from 'App/AppContext';
import { globalNotAuthorizedURL } from 'globalConstants';
import { ReactNode, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { FeatureTypes, hasUserFeature, hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';

interface IProtectedRouteProps {
  featureType: FeatureTypes;
  //the route stays open while a backup restore holds the organization (the API opens only the Backup reads then)
  allowTenantRestoreHold?: boolean;
  children: ReactNode;
}

//
// A route element that renders its children only for a user who may open the feature; anyone else lands on
// the not-authorized page. No navigation during render: the redirect is a <Navigate replace /> element.
//
const ProtectedRoute = (props: IProtectedRouteProps) => {
  const appContext = useContext(AppContext);
  const user = appContext.user;

  //There is no user or login information
  if (!user || !user.login) {
    return <Navigate to={globalNotAuthorizedURL} replace />;
  }

  //A backup restore holds the organization: the root shows the admin the Backup page and anyone else the reason
  if (user.login.isHeldByRestore() && !props.allowTenantRestoreHold) {
    return <Navigate to="/" replace />;
  }

  //A registered organization whose trial or subscription ended: users with a role keep access, so an admin
  //can still reach the administration
  const tenantIsRegisteredAndUserHasRole =
    user.login.tenantId.length > 0 &&
    user.login.tenantLicensed === false &&
    user.login.userLicensed === false &&
    hasUserFeatureGenericManager(appContext);

  //No licensed user
  if (!user.login.userLicensed && !tenantIsRegisteredAndUserHasRole) {
    return <Navigate to={globalNotAuthorizedURL} replace />;
  }

  //No valid feature for user
  if (!hasUserFeature(appContext, props.featureType)) {
    return <Navigate to={globalNotAuthorizedURL} replace />;
  }

  return <>{props.children}</>;
};

export default ProtectedRoute;
